/**
 * StepHintGenerator
 *
 * Client-side component for generating context-aware step hints.
 * When `ai.generate_hint: true` in YAML step config, fetches a contextual
 * hint from the tour-ai-hint API and displays it as an "AI Tip" callout.
 *
 * Hints are cached per step per session to avoid redundant API calls.
 */

'use client';

import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StepHintGeneratorProps {
  /** The hint text to display */
  hint: string | null;
  /** Whether the hint is currently being loaded */
  loading: boolean;
  /** Whether AI is available */
  aiAvailable: boolean;
  /** Whether the step has ai.generate_hint enabled */
  hintEnabled: boolean;
  /** Optional className override */
  className?: string;
}

export function StepHintGenerator({
  hint,
  loading,
  aiAvailable,
  hintEnabled,
  className,
}: StepHintGeneratorProps) {
  const [dismissedHints, setDismissedHints] = useState<Set<string>>(new Set());

  const isDismissed = hint ? dismissedHints.has(hint) : false;

  const dismiss = () => {
    if (hint) {
      setDismissedHints((prev) => new Set(prev).add(hint));
    }
  };

  if (!hintEnabled || !aiAvailable || isDismissed) {
    return null;
  }

  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/80 p-3 dark:border-violet-800 dark:bg-violet-900/30',
          className
        )}
      >
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-violet-500" />
        <span className="text-xs text-violet-700 dark:text-violet-300">Generating AI tip...</span>
      </div>
    );
  }

  if (!hint) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 p-3 dark:border-violet-800 dark:from-violet-900/40 dark:to-purple-900/40',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-300">
            AI Tip
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-violet-900 dark:text-violet-100">{hint}</p>
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 text-violet-400 transition-colors hover:text-violet-600 dark:text-violet-300 dark:hover:text-violet-100"
          aria-label="Dismiss AI tip"
        >
          <span className="text-xs">✕</span>
        </button>
      </div>
    </div>
  );
}
